// ============================================
// Codebase Intelligence — Scanner Core
// Pure functions for debt scanning: pattern
// definitions, scoring, hotspot calculation,
// and suggested fix generation.
// Shared by scripts/scanDebt.cjs and tests.
// ============================================

import type { DebtType, DebtSeverity } from '../types/ops';

// ── Pattern Definitions ──

export interface DebtPattern {
  pattern: RegExp;
  severity: DebtSeverity;
  desc: string;
}

export const DEBT_PATTERNS: Record<DebtType, DebtPattern[]> = {
  HARDCODED_VALUE: [
    { pattern: /"hemologix-primary"/g, severity: 'medium', desc: 'Hardcoded brand ID — should be dynamic' },
    { pattern: /localhost:\d{4,5}/g, severity: 'low', desc: 'Hardcoded localhost URL' },
  ],
  STUBBED_FUNCTION: [
    { pattern: /\/\/\s*TODO\b/gi, severity: 'medium', desc: 'TODO marker — incomplete implementation' },
    { pattern: /\/\/\s*FIXME\b/gi, severity: 'medium', desc: 'FIXME marker — known issue' },
    { pattern: /\/\/\s*HACK\b/gi, severity: 'medium', desc: 'HACK marker — workaround in place' },
    { pattern: /\/\/\s*STUB\b/gi, severity: 'high', desc: 'Stubbed implementation' },
    { pattern: /throw new Error\(["']not implemented/gi, severity: 'high', desc: 'Not-implemented error thrown' },
    { pattern: /\/\/\s*DEFERRED\s*\u2014/gi, severity: 'low', desc: 'Deferred implementation' },
  ],
  TYPE_WORKAROUND: [
    { pattern: /\bas any\b/g, severity: 'medium', desc: "Type cast to 'any' — bypasses type safety" },
    { pattern: /@ts-ignore/g, severity: 'high', desc: '@ts-ignore suppresses type errors' },
    { pattern: /@ts-expect-error/g, severity: 'medium', desc: '@ts-expect-error type suppression' },
  ],
  MISSING_ERROR_HANDLING: [
    { pattern: /catch\s*\([^)]*\)\s*\{\s*\}/g, severity: 'high', desc: 'Empty catch block — errors silently swallowed' },
    { pattern: /\.catch\(\s*\(\)\s*=>\s*\{\s*\}\s*\)/g, severity: 'high', desc: 'Empty promise catch — errors silently swallowed' },
    { pattern: /\.catch\(\s*\(\s*\)\s*=>\s*null\s*\)/g, severity: 'medium', desc: 'Promise catch returns null — error lost' },
  ],
  PLACEHOLDER_UI: [
    { pattern: /["']Coming soon["']/gi, severity: 'low', desc: "Placeholder 'Coming soon' text in UI" },
    { pattern: /["']Not yet available["']/gi, severity: 'low', desc: 'Placeholder text in UI' },
    { pattern: />--<\//g, severity: 'low', desc: "Placeholder '--' displayed in UI" },
    { pattern: /["']Build metrics available after/g, severity: 'low', desc: 'Placeholder text awaiting future feature' },
  ],
  DEAD_CODE: [
    { pattern: /\/\/\s*export\s+(function|const|class|interface|type)\s/g, severity: 'low', desc: 'Commented-out export' },
  ],
  HARDCODED_PROMPT: [
    { pattern: /system.*?:\s*[`"']You are/gi, severity: 'high', desc: 'Hardcoded AI system prompt — should be in prompt library' },
    { pattern: /role:\s*["']system["'].*content:\s*[`"']/g, severity: 'medium', desc: 'Inline system prompt content' },
  ],
  STALE_REFERENCE: [
    { pattern: /\/\/.*\bdeprecated\b/gi, severity: 'medium', desc: 'Deprecated code marker' },
    { pattern: /\/\/.*\bremove after\b/gi, severity: 'medium', desc: 'Code marked for future removal' },
  ],
  MISSING_BRAND_SCOPING: [
    { pattern: /collection\(["']products["']\)/g, severity: 'high', desc: 'Unscoped Firestore collection — needs brands/{brandId}/ prefix' },
    { pattern: /collection\(["']patients["']\)/g, severity: 'high', desc: 'Unscoped patients collection' },
    { pattern: /collection\(["']orders["']\)/g, severity: 'high', desc: 'Unscoped orders collection' },
    { pattern: /collection\(["']documents["']\)/g, severity: 'medium', desc: 'Unscoped documents collection — verify brand scoping' },
  ],
  SECURITY_GAP: [
    { pattern: /eval\(/g, severity: 'critical', desc: 'eval() usage — code injection risk' },
    { pattern: /innerHTML\s*=/g, severity: 'critical', desc: 'innerHTML assignment — XSS risk' },
    { pattern: /dangerouslySetInnerHTML/g, severity: 'high', desc: 'dangerouslySetInnerHTML — XSS risk if input not sanitized' },
  ],
};

// Severity weights for score calculation
export const SEVERITY_WEIGHT: Record<DebtSeverity, number> = {
  critical: 15,
  high: 5,
  medium: 2,
  low: 0.5,
};

// ── Finding Interface ──

export interface RawFinding {
  type: DebtType;
  severity: DebtSeverity;
  file: string;
  line: number;
  description: string;
  snippet: string;
}

// ── Scanner ──

export function scanContent(content: string, filePath: string): RawFinding[] {
  const lines = content.split('\n');
  const findings: RawFinding[] = [];
  const seenLines = new Set<string>();

  for (const [debtType, patterns] of Object.entries(DEBT_PATTERNS) as [DebtType, DebtPattern[]][]) {
    for (const { pattern, severity, desc } of patterns) {
      const regex = new RegExp(pattern.source, pattern.flags);
      let match;
      while ((match = regex.exec(content)) !== null) {
        const upToMatch = content.slice(0, match.index);
        const lineNum = upToMatch.split('\n').length;

        const dedupKey = `${debtType}:${lineNum}`;
        if (seenLines.has(dedupKey)) continue;
        seenLines.add(dedupKey);

        const lineContent = lines[lineNum - 1] || '';
        if (debtType === 'HARDCODED_VALUE' && lineContent.trim().startsWith('//')) continue;
        if (debtType === 'SECURITY_GAP' && lineContent.trim().startsWith('//')) continue;
        if (debtType === 'TYPE_WORKAROUND' && filePath.endsWith('.d.ts')) continue;

        findings.push({
          type: debtType,
          severity,
          file: filePath,
          line: lineNum,
          description: desc,
          snippet: lineContent.trim().slice(0, 120),
        });
      }
    }
  }

  return findings;
}

// ── Score Calculator ──

export function calculateDebtScore(findings: RawFinding[], fileCount: number): number {
  let totalPoints = 0;
  for (const f of findings) {
    totalPoints += SEVERITY_WEIGHT[f.severity] || 1;
  }
  const normalizedPenalty = Math.round((totalPoints * 50) / Math.max(fileCount, 1));
  return Math.max(0, Math.min(100, 100 - normalizedPenalty));
}

// ── Hotspot Calculator ──

export function calculateHotspots(findings: RawFinding[]): { file: string; count: number }[] {
  const fileCounts: Record<string, number> = {};
  for (const f of findings) {
    fileCounts[f.file] = (fileCounts[f.file] || 0) + 1;
  }
  return Object.entries(fileCounts)
    .map(([file, count]) => ({ file, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);
}

// ── Suggested Fix Generator ──

export function suggestFix(type: DebtType, snippet: string): string | undefined {
  switch (type) {
    case 'HARDCODED_VALUE':
      return 'Extract to config or pass as parameter from brand context';
    case 'STUBBED_FUNCTION':
      if (/TODO/i.test(snippet)) return 'Implement or create a tracking issue';
      if (/DEFERRED/i.test(snippet)) return 'Track in project backlog';
      return 'Complete implementation';
    case 'TYPE_WORKAROUND':
      if (/@ts-ignore/.test(snippet)) return 'Fix the underlying type error instead of suppressing';
      return 'Replace with proper type annotation';
    case 'MISSING_ERROR_HANDLING':
      return 'Add error logging or user-facing error handling';
    case 'PLACEHOLDER_UI':
      return 'Replace with real content or conditionally hide';
    case 'DEAD_CODE':
      return 'Remove if no longer needed';
    case 'HARDCODED_PROMPT':
      return 'Move to prompt library (Firestore prompts collection)';
    case 'STALE_REFERENCE':
      return 'Update or remove deprecated reference';
    case 'MISSING_BRAND_SCOPING':
      return 'Add brands/{brandId}/ prefix to Firestore path for multi-tenant';
    case 'SECURITY_GAP':
      return 'Replace with safe alternative (textContent, sanitized HTML, etc.)';
    default:
      return undefined;
  }
}
